import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { demosApi, workQueryKeys } from '@/features/work/api';
import type {
  DemosFilters,
  CreateDemoPayload,
  UpdateDemoPayload,
  FeedbackPayload,
} from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useDemos(filters: DemosFilters = {}) {
  return useQuery({
    queryKey: workQueryKeys.demos.list(filters),
    queryFn: () => demosApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useDemo(id: string | null) {
  return useQuery({
    queryKey: workQueryKeys.demos.detail(id),
    queryFn: () => demosApi.detail(id as string),
    enabled: !!id,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useDemoMutations() {
  const queryClient = useQueryClient();
  const invalidate = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: workQueryKeys.demos.root });
    if (id) queryClient.invalidateQueries({ queryKey: workQueryKeys.demos.detail(id) });
  };

  const createDemo = useMutation({ mutationFn: (payload: CreateDemoPayload) => demosApi.create(payload), onSuccess: () => invalidate() });
  const updateDemo = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDemoPayload }) => demosApi.update(id, payload),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
  const assignDemo = useMutation({
    mutationFn: ({ id, assigned_to }: { id: string; assigned_to: string | null }) => demosApi.assign(id, assigned_to),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
  const addComment = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) => demosApi.addComment(id, comment),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
  const addFeedback = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: FeedbackPayload }) => demosApi.addFeedback(id, payload),
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
  const deleteDemo = useMutation({ mutationFn: (id: string) => demosApi.remove(id), onSuccess: () => invalidate() });

  return { createDemo, updateDemo, assignDemo, addComment, addFeedback, deleteDemo };
}
